import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AuthService } from '../services/auth.service';
import { ChangePasswordRequest, landingRouteForRole } from '../types/auth.types';
import { StorageService } from '../services/storage.service';
import { Input } from '../../common';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const PasswordChangeScreen: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<{
    currentPassword: string;
    newPassword: string;
  }>({
    currentPassword: '',
    newPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    general?: string;
  }>({});
  const [success, setSuccess] = useState<string>('');
  const [passwordValidation, setPasswordValidation] = useState<{
    hasMinLength: boolean;
    hasUpperCase: boolean;
    hasLowerCase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  }>({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  /**
   * Valida una contraseña según los criterios de seguridad
   */
  const validatePassword = (password: string) => {
    return {
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  };

  /**
   * Valida el formulario de cambio de contraseña
   */
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'La contraseña actual es requerida';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else if (!Object.values(passwordValidation).every(Boolean)) {
      newErrors.newPassword = 'La contraseña no cumple con los requisitos de seguridad';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña debe ser diferente a la actual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja el cambio de valores en los campos del formulario
   */
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    // Limpiar mensaje de éxito
    if (success) {
      setSuccess('');
    }
  };

  /**
   * Maneja el cambio de la nueva contraseña con validación en tiempo real
   */
  const handleNewPasswordChange = (value: string) => {
    setFormData(prev => ({ ...prev, newPassword: value }));
    setPasswordValidation(validatePassword(value));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors.newPassword) {
      setErrors(prev => ({ ...prev, newPassword: undefined }));
    }
    
    // Limpiar mensaje de éxito
    if (success) {
      setSuccess('');
    }
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccess('');

    try {
      const passwordData: ChangePasswordRequest = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      };

      await AuthService.changePassword(passwordData);
      
      // Limpiar formulario
      setFormData({
        currentPassword: '',
        newPassword: ''
      });
      setErrors({});
      setPasswordValidation({
        hasMinLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false,
      });
      
      setSuccess('Contraseña cambiada exitosamente');
      
      // Redirigir según el ROL tras un breve delay (mismo criterio que el
      // login: /users sólo para quien administra usuarios).
      setTimeout(() => {
        navigate(landingRouteForRole(StorageService.getUser()?.role));
      }, 2000);
      
    } catch (error: any) {
      console.error('Error changing password:', error);
      const errorMessage = error?.message || 'Error al cambiar la contraseña';
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const header = (
    <div className="text-center py-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-3">Cambio de Contraseña</h1>
      <p className="text-gray-600">Su contraseña ha expirado y debe cambiarla para continuar</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card header={header} className="shadow-2xl border-0">
          <form onSubmit={handleSubmit} className="space-y-6">
            {errors.general && (
              <Message 
                severity="error" 
                text={errors.general} 
                className="w-full"
              />
            )}

            {success && (
              <Message 
                severity="success" 
                text={success} 
                className="w-full"
              />
            )}

            {/* Contraseña Actual */}
            <Input
              id="currentPassword"
              label="Contraseña Actual"
              type="password"
              value={formData.currentPassword}
              onChange={(value) => handleInputChange('currentPassword', value)}
              placeholder="Ingrese su contraseña actual"
              error={errors.currentPassword}
              disabled={isLoading}
              required
              showPasswordToggle
            />

            {/* Nueva Contraseña */}
            <div className="field">
              <Input
                id="newPassword"
                label="Nueva Contraseña"
                type="password"
                value={formData.newPassword}
                onChange={handleNewPasswordChange}
                placeholder="Ingrese su nueva contraseña"
                error={errors.newPassword}
                disabled={isLoading}
                required
                showPasswordToggle
              />
              
              {/* Indicadores de validación de contraseña */}
              {formData.newPassword && (
                <div className="mt-3 space-y-2 bg-gray-50 p-3 rounded-lg">
                  <div className={`text-sm font-medium ${passwordValidation.hasMinLength ? 'text-green-600' : 'text-red-600'}`}>
                    {passwordValidation.hasMinLength ? '✓' : '✗'} Al menos 8 caracteres
                  </div>
                  <div className={`text-sm font-medium ${passwordValidation.hasUpperCase ? 'text-green-600' : 'text-red-600'}`}>
                    {passwordValidation.hasUpperCase ? '✓' : '✗'} Al menos una letra mayúscula
                  </div>
                  <div className={`text-sm font-medium ${passwordValidation.hasLowerCase ? 'text-green-600' : 'text-red-600'}`}>
                    {passwordValidation.hasLowerCase ? '✓' : '✗'} Al menos una letra minúscula
                  </div>
                  <div className={`text-sm font-medium ${passwordValidation.hasNumber ? 'text-green-600' : 'text-red-600'}`}>
                    {passwordValidation.hasNumber ? '✓' : '✗'} Al menos un número
                  </div>
                  <div className={`text-sm font-medium ${passwordValidation.hasSpecialChar ? 'text-green-600' : 'text-red-600'}`}>
                    {passwordValidation.hasSpecialChar ? '✓' : '✗'} Al menos un carácter especial
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              label={isLoading ? 'Cambiando contraseña...' : 'Cambiar Contraseña'}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 border-0"
              disabled={isLoading || !formData.currentPassword || !formData.newPassword || !Object.values(passwordValidation).every(Boolean)}
            >
              {isLoading && <ProgressSpinner style={{ width: '20px', height: '20px' }} />}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-500">
                Complete el cambio de contraseña para acceder al sistema
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PasswordChangeScreen;
